import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronRight, FileUp, LoaderCircle, Plus, Search, Sparkles, WandSparkles } from 'lucide-react'
import {
  importDirectorSkillMarkdown,
  directorSkillPublicSlug,
  installDirectorSkill,
  listAvailableDirectorSkills,
  listDirectorSkillCatalog,
  listInstalledDirectorSkills,
  listOwnedDirectorSkills,
  readDirectorSkillStructuredError,
  resolveRuntimeDirectorSkill,
  uninstallDirectorSkill,
  updateDirectorSkillInstallation,
  type DirectorSkillSummary,
} from '../../api/directorSkills'
import { createNadDebug } from '../../utils/debugLog'
import { cn } from '../../lib/utils'
import { useSkillPickerAnalytics } from '../../analytics/chatHeaderAnalytics'
import Modal from '../ui/Modal'
import { useTranslation } from 'react-i18next'
import { SkillCoverMedia } from './SkillCoverMedia'
import { SkillDetail } from './SkillDetail'

const skillLog = createNadDebug('dw:director-skills:picker')

interface SkillPickerProps {
  selectedSkill: DirectorSkillSummary | null
  onSelect: (skill: DirectorSkillSummary | null) => void
  onCreateWithDirector: () => void
  onEditWithDirector?: (skill: DirectorSkillSummary) => void
  disabled?: boolean
  triggerClassName?: string
  /** Icon size/colour override; defaults to `h-5 w-5`. */
  triggerIconClassName?: string
  showTriggerLabel?: boolean
  onTriggerMouseEnter?: () => void
}

type SkillTab = 'builtin' | 'installed' | 'mine' | 'discover'
type SkillUploadPhase = 'idle' | 'uploading' | 'rewriting'
const SKILL_TABS: readonly SkillTab[] = ['builtin', 'installed', 'mine', 'discover']

function skillMatchesTab(skill: DirectorSkillSummary, tab: SkillTab): boolean {
  return (
    tab === 'discover' ||
    (tab === 'builtin' && skill.source === 'builtin') ||
    (tab === 'installed' && skill.source === 'published' && skill.installed === true) ||
    (tab === 'mine' && skill.source === 'user')
  )
}

export function SkillPicker({
  selectedSkill,
  onSelect,
  onCreateWithDirector,
  onEditWithDirector,
  disabled = false,
  triggerClassName,
  triggerIconClassName = 'h-5 w-5',
  showTriggerLabel = false,
  onTriggerMouseEnter,
}: SkillPickerProps) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [tab, setTab] = useState<SkillTab>('builtin')
  const [skills, setSkills] = useState<DirectorSkillSummary[]>([])
  const [catalogSkills, setCatalogSkills] = useState<DirectorSkillSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [uploadPhase, setUploadPhase] = useState<SkillUploadPhase>('idle')
  const uploading = uploadPhase !== 'idle'
  const [error, setError] = useState<string | null>(null)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [detail, setDetail] = useState<DirectorSkillSummary | null>(null)
  const [hoveredSkill, setHoveredSkill] = useState<DirectorSkillSummary | null>(null)
  const [query, setQuery] = useState('')
  const [panelPosition, setPanelPosition] = useState<{
    left: number
    bottom: number
    previewSide: 'left' | 'right'
  }>({ left: 12, bottom: 56, previewSide: 'right' })
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelPortalRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const galleryContentRef = useRef<HTMLDivElement>(null)
  const detailUseRef = useRef<HTMLButtonElement>(null)
  const compactTabPanelId = useId()
  const galleryTabPanelId = useId()
  // RFC-1577: skills on the current tab once a catalog load has completed.
  const [catalogLoaded, setCatalogLoaded] = useState(false)
  const tabSkillCount = useMemo(() => {
    if (!catalogLoaded) return undefined
    return tab === 'discover' ? catalogSkills.length : skills.filter((skill) => skillMatchesTab(skill, tab)).length
  }, [catalogLoaded, catalogSkills, skills, tab])
  const installedSkillCount = useMemo(
    () => (catalogLoaded ? skills.filter((skill) => skill.source === 'published' && skill.installed === true).length : undefined),
    [catalogLoaded, skills],
  )
  const analytics = useSkillPickerAnalytics({ visible: open || galleryOpen, tab, tabSkillCount, installedSkillCount })
  // 1-based list position of the skill whose detail view is open, for its Use click.
  const detailPositionRef = useRef<number | undefined>(undefined)

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect()
    if (!trigger) return
    const panelWidth = Math.min(448, window.innerWidth - 24)
    const left = Math.min(Math.max(12, trigger.left + trigger.width / 2 - panelWidth / 2), window.innerWidth - panelWidth - 12)
    setPanelPosition({
      left,
      bottom: Math.max(12, window.innerHeight - trigger.top + 8),
      previewSide: left >= 304 ? 'left' : 'right',
    })
  }, [])

  const loadSkills = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [available, owned, installed, catalog] = await Promise.all([
        listAvailableDirectorSkills(),
        listOwnedDirectorSkills(),
        listInstalledDirectorSkills(),
        listDirectorSkillCatalog(),
      ])
      const builtins = available.filter((skill) => skill.source === 'builtin')
      const ownedIds = new Set(owned.map((skill) => skill.skillId))
      const externalInstallations = installed.filter((skill) => !ownedIds.has(skill.skillId))
      const installedById = new Map(externalInstallations.map((skill) => [skill.skillId, skill]))
      const visibleCatalog = catalog
        .filter((skill) => !ownedIds.has(skill.skillId))
        .map((skill) => {
          const installation = installedById.get(skill.skillId)
          return installation
            ? {
                ...skill,
                installed: true,
                enabled: installation.enabled,
                available: installation.available,
                installedRef: installation.ref,
                installedVersionId: installation.versionId,
                installedVersion: installation.version,
              }
            : {
                ...skill,
                installed: skill.installed === true,
                installedRef: undefined,
                installedVersion: undefined,
              }
        })
      setSkills([...builtins, ...owned, ...externalInstallations])
      setCatalogSkills(visibleCatalog)
      setCatalogLoaded(true)
      skillLog({
        event: 'skills-loaded',
        trace_id: 'director-skills-picker:catalog',
        requested: 'available-owned-installed-catalog',
        resolved: builtins.length + owned.length + externalInstallations.length + visibleCatalog.length,
        outcome: 'success',
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
      skillLog({
        event: 'skills-load-failed',
        trace_id: 'director-skills-picker:catalog',
        requested: 'available-skills',
        resolved: 0,
        outcome: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open || galleryOpen) void loadSkills()
  }, [galleryOpen, loadSkills, open])

  useEffect(() => {
    if (!open) return
    updatePanelPosition()
    window.requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLButtonElement>(`[data-skill-tab="${tab}"]`)?.focus()
    })
    const closeOutside = (event: MouseEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target) && !panelPortalRef.current?.contains(event.target)) {
        setOpen(false)
        setCreateOpen(false)
        setHoveredSkill(null)
      }
    }
    document.addEventListener('mousedown', closeOutside)
    window.addEventListener('resize', updatePanelPosition)
    window.addEventListener('scroll', updatePanelPosition, true)
    return () => {
      document.removeEventListener('mousedown', closeOutside)
      window.removeEventListener('resize', updatePanelPosition)
      window.removeEventListener('scroll', updatePanelPosition, true)
    }
  }, [open, tab, updatePanelPosition])

  useEffect(() => {
    if (!galleryOpen) return
    window.requestAnimationFrame(() => {
      if (detail) detailUseRef.current?.focus()
      else {
        galleryContentRef.current?.querySelector<HTMLButtonElement>(`[data-skill-tab="${tab}"]`)?.focus()
      }
    })
  }, [detail, galleryOpen, tab])

  const visibleSkills = useMemo(
    () =>
      (tab === 'discover' ? catalogSkills : skills).filter((skill) => {
        const matchesTab = skillMatchesTab(skill, tab)
        const normalizedQuery = query.trim().toLocaleLowerCase()
        return matchesTab && (!normalizedQuery || `${skill.displayName} ${directorSkillPublicSlug(skill)} ${skill.description}`.toLocaleLowerCase().includes(normalizedQuery))
      }),
    [catalogSkills, query, skills, tab],
  )

  const closeGallery = useCallback(() => {
    setGalleryOpen(false)
    setDetail(null)
    window.requestAnimationFrame(() => triggerRef.current?.focus())
  }, [])

  const openDetail = useCallback((skill: DirectorSkillSummary, position?: number) => {
    detailPositionRef.current = position
    analytics.detailViewed(skill)
    setDetail(skill)
    setGalleryOpen(true)
    setOpen(false)
    setHoveredSkill(null)
    skillLog({
      event: 'skill-detail:opened',
      trace_id: `director-skills-detail:${skill.skillId}`,
      skill_id: skill.skillId,
      version_id: skill.versionId,
      cover_kind: skill.cover?.kind ?? 'none',
      source: skill.source,
      outcome: 'visible',
    })
  }, [analytics])

  const choose = useCallback(
    // `upload` = auto-selected after the user's upload; the server logs the
    // upload as director_skill_create_result, so no select is logged.
    (skill: DirectorSkillSummary, source: 'picker_list' | 'skill_detail' | 'upload', position?: number) => {
      const runtimeSkill = resolveRuntimeDirectorSkill(skill)
      if (!runtimeSkill) {
        skillLog({
          event: 'skill-runtime-selection-blocked',
          trace_id: `director-skills-selection:${skill.skillId}`,
          skill_id: skill.skillId,
          version_id: skill.versionId,
          installed: skill.installed ?? false,
          enabled: skill.enabled ?? null,
          available: skill.available ?? null,
          outcome: 'details-only',
        })
        openDetail(skill, position)
        return
      }
      const selectionChanged =
        selectedSkill?.ref !== runtimeSkill.ref || selectedSkill?.versionId !== runtimeSkill.versionId
      // Before closing, so the row belongs to this open.
      if (selectionChanged && source !== 'upload') analytics.selected(runtimeSkill, source, position)
      onSelect(runtimeSkill)
      setOpen(false)
      setGalleryOpen(false)
      setDetail(null)
      setHoveredSkill(null)
      window.requestAnimationFrame(() => triggerRef.current?.focus())
      skillLog({
        event: 'skill-selected',
        trace_id: 'director-skills-picker:selection',
        skill_ref: runtimeSkill.ref,
        version_id: runtimeSkill.versionId,
        source: runtimeSkill.source,
        outcome: 'selected',
      })
    },
    [analytics, onSelect, openDetail, selectedSkill?.ref, selectedSkill?.versionId],
  )

  const updateSkill = useCallback(
    (next: DirectorSkillSummary) => {
      setSkills((current) => current.map((skill) => (skill.skillId === next.skillId ? next : skill)))
      setDetail(next)
      if (selectedSkill?.skillId === next.skillId) {
        const runtimeSkill = resolveRuntimeDirectorSkill(next)
        if (!runtimeSkill) analytics.cleared(selectedSkill)
        onSelect(runtimeSkill)
      }
    },
    [analytics, onSelect, selectedSkill],
  )

  const installSkill = useCallback(
    async (skill: DirectorSkillSummary) => {
      const installed = await installDirectorSkill(skill.skillId, {
        version_id: skill.versionId,
      })
      setSkills((current) => [installed, ...current.filter((item) => item.skillId !== installed.skillId)])
      setCatalogSkills((current) =>
        current.map((item) =>
          item.skillId === installed.skillId
            ? {
                ...item,
                installed: true,
                enabled: installed.enabled,
                available: installed.available,
                installedRef: installed.ref,
                installedVersionId: installed.versionId,
                installedVersion: installed.version,
              }
            : item,
        ),
      )
      if (selectedSkill?.skillId === installed.skillId) {
        const runtimeSkill = resolveRuntimeDirectorSkill(installed)
        if (!runtimeSkill) analytics.cleared(selectedSkill)
        onSelect(runtimeSkill)
      }
      setDetail(installed)
      skillLog({
        event: 'skill-installed',
        trace_id: `director-skills-install:${skill.skillId}`,
        skill_id: skill.skillId,
        version_id: installed.versionId,
        outcome: 'installed',
      })
    },
    [analytics, onSelect, selectedSkill],
  )

  const updateInstallation = useCallback(
    async (skill: DirectorSkillSummary) => {
      const installed = await updateDirectorSkillInstallation(skill.skillId)
      setSkills((current) => current.map((item) => (item.skillId === installed.skillId ? installed : item)))
      setCatalogSkills((current) =>
        current.map((item) =>
          item.skillId === installed.skillId
            ? {
                ...item,
                installed: true,
                enabled: installed.enabled,
                available: installed.available,
                updateAvailable: installed.updateAvailable,
                installedRef: installed.ref,
                installedVersionId: installed.versionId,
                installedVersion: installed.version,
              }
            : item,
        ),
      )
      if (selectedSkill?.skillId === installed.skillId) {
        const runtimeSkill = resolveRuntimeDirectorSkill(installed)
        if (!runtimeSkill) analytics.cleared(selectedSkill)
        onSelect(runtimeSkill)
      }
      setDetail(installed)
      skillLog({
        event: 'skill-installation-updated',
        trace_id: `director-skills-install:${skill.skillId}`,
        skill_id: skill.skillId,
        version_id: installed.versionId,
        enabled: installed.enabled,
        outcome: 'updated',
      })
    },
    [analytics, onSelect, selectedSkill],
  )

  const uninstallSkill = useCallback(
    async (skill: DirectorSkillSummary) => {
      await uninstallDirectorSkill(skill.skillId)
      setSkills((current) => current.filter((item) => item.skillId !== skill.skillId || item.source !== 'published'))
      setCatalogSkills((current) =>
        current.map((item) =>
          item.skillId === skill.skillId
            ? {
                ...item,
                installed: false,
                enabled: undefined,
                available: true,
                installedRef: undefined,
                installedVersionId: undefined,
                installedVersion: undefined,
              }
            : item,
        ),
      )
      if (selectedSkill?.skillId === skill.skillId) {
        analytics.cleared(selectedSkill)
        onSelect(null)
        skillLog({
          event: 'skill-selection-cleared',
          trace_id: `director-skills-selection:${skill.skillId}`,
          skill_id: skill.skillId,
          reason: 'uninstalled',
          outcome: 'cleared',
        })
      }
      setDetail((current) => {
        if (current?.skillId !== skill.skillId) return current
        const catalog = catalogSkills.find((item) => item.skillId === skill.skillId)
        const next = catalog ?? current
        return {
          ...next,
          installed: false,
          enabled: undefined,
          available: true,
          installedRef: undefined,
          installedVersionId: undefined,
          installedVersion: undefined,
        }
      })
      skillLog({
        event: 'skill-uninstalled',
        trace_id: `director-skills-install:${skill.skillId}`,
        skill_id: skill.skillId,
        outcome: 'uninstalled',
      })
    },
    [analytics, catalogSkills, onSelect, selectedSkill],
  )

  const tabLabel = useCallback(
    (value: SkillTab): string => {
      if (value === 'builtin') return t('skillPicker.builtin')
      if (value === 'installed') return t('skillPicker.installed')
      if (value === 'mine') return t('skillPicker.mine')
      return t('skillPicker.discover')
    },
    [t],
  )

  const handleTabKeyDown = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>, current: SkillTab) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const currentIndex = SKILL_TABS.indexOf(current)
    const direction = event.key === 'ArrowRight' ? 1 : -1
    const next = SKILL_TABS[(currentIndex + direction + SKILL_TABS.length) % SKILL_TABS.length]
    const tablist = event.currentTarget.parentElement
    setTab(next)
    window.requestAnimationFrame(() => {
      tablist?.querySelector<HTMLButtonElement>(`[data-skill-tab="${next}"]`)?.focus()
    })
  }, [])

  const upload = useCallback(
    async (file: File) => {
      setUploadPhase('uploading')
      setError(null)
      try {
        const skill = await importDirectorSkillMarkdown(file, {
          onRewriteRequired: () => setUploadPhase('rewriting'),
        })
        setSkills((current) => [skill, ...current.filter((item) => item.ref !== skill.ref)])
        choose(skill, 'upload')
        skillLog({
          event: 'skill-uploaded',
          trace_id: 'director-skills-picker:upload',
          skill_ref: skill.ref,
          version_id: skill.versionId,
          content_bytes: file.size,
          outcome: 'active',
        })
      } catch (reason) {
        const structured = reason instanceof Error ? readDirectorSkillStructuredError(reason) : null
        const message = structured?.code === 'skill_document_too_large' && structured.limit !== undefined && structured.actual !== undefined
          ? t('skillPicker.skillDocumentTooLarge', {
              actual: structured.actual,
              limit: structured.limit,
            })
          : structured?.field && structured.limit !== undefined && structured.actual !== undefined
            ? t('skillPicker.frontmatterFieldTooLong', {
              field: structured.field,
              actual: structured.actual,
              limit: structured.limit,
            })
            : reason instanceof Error
              ? reason.message
              : String(reason)
        setError(
          structured || message.startsWith('Choose a Markdown') || message.startsWith('Markdown skill files')
            ? message
            : t('skillPicker.uploadFailed', { reason: message }),
        )
        skillLog({
          event: 'skill-upload-failed',
          trace_id: 'director-skills-picker:upload',
          content_bytes: file.size,
          outcome: 'error',
        })
      } finally {
        setUploadPhase('idle')
      }
    },
    [choose, t],
  )

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        onMouseEnter={onTriggerMouseEnter}
        disabled={disabled}
        // cn/twMerge: caller-supplied trigger classes win over the label defaults
        // (e.g. a host pill's responsive text size and gap).
        className={cn(
          showTriggerLabel ? 'inline-flex items-center gap-2 text-base' : '',
          triggerClassName ??
          'rounded-md p-2 text-text-muted transition-colors hover:bg-primary/20 hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50',
        )}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? 'director-skill-picker-panel' : undefined}
        aria-label={t('skillPicker.choose')}
        data-testid="director-skill-picker-trigger"
        title={t('skillPicker.choose')}
      >
        <WandSparkles className={`shrink-0 ${triggerIconClassName}`} strokeWidth={1.5} aria-hidden="true" />
        {showTriggerLabel && <span>{t('skillPicker.skills')}</span>}
        {selectedSkill && <span className="sr-only">{selectedSkill.displayName}</span>}
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelPortalRef}
            className="fixed z-[1200] w-[min(448px,calc(100vw-24px))]"
            style={{ left: panelPosition.left, bottom: panelPosition.bottom }}
            data-testid="director-skill-picker-portal"
          >
            {hoveredSkill && (
              <div
                className={`pointer-events-none absolute bottom-0 w-72 ${panelPosition.previewSide === 'left' ? 'right-full mr-2' : 'left-full ml-2'}`}
                data-testid="director-skill-hover-preview"
              >
                {hoveredSkill.cover && (
                  <SkillCoverMedia key={hoveredSkill.ref} cover={hoveredSkill.cover} title={hoveredSkill.displayName} mode="hover" />
                )}
                <div className={`${hoveredSkill.cover ? 'rounded-b-xl border-x border-b' : 'rounded-xl border'} border-border bg-background px-4 py-3 shadow-2xl`}>
                  <p className="truncate text-base font-medium text-foreground">{hoveredSkill.displayName}</p>
                  <p className="mt-1 line-clamp-2 text-xs font-medium leading-relaxed text-muted-foreground">{hoveredSkill.description}</p>
                </div>
              </div>
            )}
            <div
              id="director-skill-picker-panel"
              ref={panelRef}
              className="overflow-visible rounded-xl border border-border bg-background shadow-2xl"
              role="dialog"
              aria-label={t('skillPicker.choose')}
              data-testid="director-skill-picker"
              onMouseLeave={() => setHoveredSkill(null)}
              onKeyDown={(event) => {
                if (event.key !== 'Escape') return
                event.stopPropagation()
                setOpen(false)
                setCreateOpen(false)
                setHoveredSkill(null)
                triggerRef.current?.focus()
              }}
            >
              <div className="flex items-center justify-between px-3 pb-2 pt-3">
                <p className="text-sm font-medium text-foreground">{t('skillPicker.skills')}</p>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setCreateOpen((value) => {
                          const next = !value
                          skillLog({
                            event: 'skill-create-menu:toggled',
                            trace_id: 'director-skills-picker:create-menu',
                            outcome: next ? 'opened' : 'closed',
                          })
                          return next
                        })
                      }
                      className="inline-flex h-8 items-center gap-1 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      data-testid="director-skill-create"
                      aria-expanded={createOpen}
                      aria-haspopup="menu"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('skillPicker.create')}
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform ${createOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                    {createOpen && (
                      <div
                        className="absolute right-0 top-full z-20 mt-2 w-64 rounded-xl border border-border bg-background p-1.5 shadow-xl"
                        role="menu"
                        data-testid="director-skill-create-menu"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setOpen(false)
                            setCreateOpen(false)
                            onCreateWithDirector()
                          }}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                          data-testid="director-skill-create-chat"
                          role="menuitem"
                        >
                          <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /> {t('skillPicker.createWithDirector')}
                        </button>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
                          data-testid="director-skill-upload"
                          role="menuitem"
                        >
                          {uploading ? (
                            <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" />
                          ) : (
                            <FileUp className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                          )}
                          {uploadPhase === 'rewriting'
                            ? t('skillPicker.rewriting')
                            : uploading
                              ? t('skillPicker.uploading')
                              : t('skillPicker.uploadMarkdown')}
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setGalleryOpen(true)
                      setOpen(false)
                    }}
                    className="inline-flex h-8 items-center gap-1 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                    data-testid="director-skill-all"
                  >
                    {t('skillPicker.all')} <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-y border-border-subtle px-3">
                <div className="flex shrink-0 gap-4 pt-2" role="tablist" aria-label={t('skillPicker.skills')}>
                  {SKILL_TABS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTab(value)}
                      onKeyDown={(event) => handleTabKeyDown(event, value)}
                      role="tab"
                      aria-selected={tab === value}
                      aria-controls={compactTabPanelId}
                      id={`${compactTabPanelId}-tab-${value}`}
                      tabIndex={tab === value ? 0 : -1}
                      className={`border-b-2 pb-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${tab === value ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                      data-testid={`director-skill-tab-${value}`}
                      data-skill-tab={value}
                    >
                      {tabLabel(value)}
                    </button>
                  ))}
                </div>
                <label className="relative my-2 min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    type="search"
                    placeholder={t('skillPicker.search')}
                    aria-label={t('skillPicker.search')}
                    className="h-8 w-full rounded-lg border border-input-border bg-input-bg pl-8 pr-2 text-xs font-medium text-foreground outline-none placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>
              </div>

              <div
                id={compactTabPanelId}
                role="tabpanel"
                aria-labelledby={`${compactTabPanelId}-tab-${tab}`}
                tabIndex={0}
                className="max-h-72 space-y-1 overflow-y-auto p-2"
              >
                {loading && <p className="px-2 py-5 text-center text-xs font-medium text-text-muted">{t('skillPicker.loading')}</p>}
                {!loading && visibleSkills.length === 0 && <p className="px-2 py-5 text-center text-xs font-medium text-text-muted">{t('skillPicker.empty')}</p>}
                {visibleSkills.map((skill, index) => (
                  <div
                    key={`${skill.ref}:${skill.versionId}`}
                    onMouseEnter={() => setHoveredSkill(skill)}
                    onMouseLeave={() => setHoveredSkill(null)}
                    className="group flex w-full items-center gap-1 rounded-lg border border-transparent pr-2 hover:border-border hover:bg-surface-2 focus-within:border-border focus-within:bg-surface-2"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (tab === 'discover') openDetail(skill, index + 1)
                        else choose(skill, 'picker_list', index + 1)
                      }}
                      onFocus={() => setHoveredSkill(skill)}
                      className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      data-testid={`director-skill-option-${skill.name}`}
                    >
                      <span className="h-7 w-7 shrink-0" data-testid={`director-skill-thumbnail-${skill.name}`}>
                        {skill.cover ? (
                          <SkillCoverMedia cover={skill.cover} title={skill.displayName} mode="thumbnail" className="h-7 w-7 rounded-lg" />
                        ) : (
                          <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground group-hover:text-primary">
                            <Sparkles className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground">{skill.displayName}</span>
                          <span className="truncate rounded bg-surface-2 px-1.5 py-0.5 text-xxs text-text-muted">/{directorSkillPublicSlug(skill)}</span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs font-medium text-muted-foreground">{skill.description}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        openDetail(skill, index + 1)
                      }}
                      onFocus={() => setHoveredSkill(skill)}
                      className="h-7 shrink-0 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground opacity-0 transition-opacity hover:border-input-border-hover hover:bg-surface-3 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 group-hover:opacity-100"
                      data-testid={`director-skill-detail-${skill.name}`}
                    >
                      {t('skillPicker.details')}
                    </button>
                  </div>
                ))}
              </div>

              {error && (
                <p role="alert" className="border-t border-border-subtle px-3 py-2 text-xs font-medium text-destructive">
                  {error}
                </p>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".md,text/markdown,text/plain"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (file) void upload(file)
                }}
                data-testid="director-skill-file-input"
              />
            </div>
          </div>,
          document.body,
        )}

      <Modal
        isOpen={galleryOpen}
        onClose={closeGallery}
        title={detail ? detail.displayName : t('skillPicker.skills')}
        size={detail && detail.source === 'builtin' && !detail.cover ? 'md' : 'xl'}
      >
        {detail ? (
          <SkillDetail
            skill={detail}
            onBack={() => setDetail(null)}
            onUse={(skill) => choose(skill, 'skill_detail', detailPositionRef.current)}
            onEditWithDirector={(skill) => {
              const runtimeSkill = resolveRuntimeDirectorSkill(skill)
              if (!runtimeSkill) return
              setGalleryOpen(false)
              setDetail(null)
              if (onEditWithDirector) onEditWithDirector(runtimeSkill)
              else onCreateWithDirector()
            }}
            onUpdated={updateSkill}
            onInstall={installSkill}
            onUpdate={updateInstallation}
            onUninstall={uninstallSkill}
            onDeleted={(skillId) => {
              setSkills((current) => current.filter((skill) => skill.skillId !== skillId))
              setCatalogSkills((current) => current.filter((skill) => skill.skillId !== skillId))
              if (selectedSkill?.skillId === skillId) {
                analytics.cleared(selectedSkill)
                onSelect(null)
                skillLog({
                  event: 'skill-selection-cleared',
                  trace_id: `director-skills-selection:${skillId}`,
                  skill_id: skillId,
                  reason: 'deleted',
                  outcome: 'cleared',
                })
              }
              setDetail(null)
            }}
            useButtonRef={detailUseRef}
          />
        ) : (
          <div ref={galleryContentRef} className="space-y-4" data-testid="director-skill-gallery">
            <div className="flex gap-1 border-b border-border-subtle" role="tablist" aria-label={t('skillPicker.skills')}>
              {SKILL_TABS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTab(value)}
                  onKeyDown={(event) => handleTabKeyDown(event, value)}
                  role="tab"
                  aria-selected={tab === value}
                  aria-controls={galleryTabPanelId}
                  id={`${galleryTabPanelId}-tab-${value}`}
                  tabIndex={tab === value ? 0 : -1}
                  className={`rounded-t-lg border-b-2 px-3 pb-2 pt-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                    tab === value ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:bg-surface-2 hover:text-foreground'
                  }`}
                  data-testid={`director-skill-gallery-tab-${value}`}
                  data-skill-tab={value}
                >
                  {tabLabel(value)}
                </button>
              ))}
            </div>
            <div id={galleryTabPanelId} role="tabpanel" aria-labelledby={`${galleryTabPanelId}-tab-${tab}`} tabIndex={0}>
              {visibleSkills.length === 0 ? (
                <p className="rounded-xl border border-border bg-surface-0 px-4 py-10 text-center text-sm text-text-muted">{t('skillPicker.empty')}</p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {visibleSkills.map((skill, index) => (
                    <button
                      key={`${skill.ref}:${skill.versionId}`}
                      type="button"
                      onClick={() => openDetail(skill, index + 1)}
                      className="group overflow-hidden rounded-xl border border-border bg-surface-0 text-left transition-colors hover:border-input-border-hover hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      data-testid={`director-skill-card-${skill.name}`}
                    >
                      {skill.cover && (
                        <SkillCoverMedia
                          cover={skill.cover}
                          title={skill.displayName}
                          mode="preview"
                          className="aspect-video w-full rounded-none border-0 border-b"
                        />
                      )}
                      <span className="block p-4">
                        <span className="flex items-start justify-between gap-3">
                          <span className="block text-lg font-medium text-foreground">{skill.displayName}</span>
                          <span className="rounded-full border border-border px-2 py-1 text-xxs uppercase tracking-wide text-text-muted">
                            {skill.source === 'builtin'
                              ? t('skillPicker.builtin')
                              : skill.source === 'published'
                                ? skill.installed
                                  ? t('skillPicker.installed')
                                  : t('skillPicker.discover')
                                : t('skillPicker.mine')}
                          </span>
                        </span>
                        <span className="mt-0.5 block text-xs font-medium text-text-muted">
                          /{directorSkillPublicSlug(skill)} · v{skill.version}
                        </span>
                        <span className="mt-2 block line-clamp-2 text-sm text-muted-foreground">{skill.description}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
